/**
 * OKRs - port from `1116/omega-club/dashboard/src/components/OKRTracker.jsx`.
 * Reads from `useTenantData(slug).okrs` + `.campaignPhases`.
 *
 * Per CHAP-84 (Phase 1C #50), wired for writes in CHAP-417.
 *
 * WRITES (CHAP-417). This page is no longer read-only. Cabinet-tier users get
 * create / edit / delete for both initiatives and key results, through the
 * CHAP-378 API in `server/routes/okrs.ts` via `hooks/useOkrs.ts`. Affordances
 * are hidden below cabinet by `<TierGuard>`, and the server independently
 * enforces `requireTier('cabinet')` — the guard is an affordance, not the
 * access control.
 *
 * THE DIVERGENCE GUARD — do not remove without reading this.
 *
 * Omega Zeta's OKRs live in TWO unsynced stores during the campaign (Jake's
 * "both for now", 2026-08-03): this one, and the legacy board dashboard's
 * Cloudflare D1. Because both stay writable, this page must state which store
 * it reads and how fresh that copy is — otherwise a board member reading it
 * would reasonably assume it is the single source of truth. That banner is the
 * mitigation, not decoration; deleting it re-creates the silent-drift defect
 * CHAP-386 exists to prevent. The wording + freshness arithmetic live in
 * `lib/okr-provenance.ts` and are unit-tested.
 *
 * Parity notes vs omega-club:
 * - **Steering Committee section omitted.** omega-club shows the Cabinet
 *   roster (`voterRoster` hardcoded). chapter-build's roster lives in
 *   `tenant_users` with `committees` array; a dedicated Roster card here
 *   needs a `/api/c/:slug/team` endpoint surface and tier-gated visibility,
 *   queued separately. (Still open after CHAP-417.)
 * - **Owner names** omega-club rendered `kr.owner` as a free-text name.
 *   chapter-build has `kr.ownerUserId` (UUID); without a users join the
 *   port still shows "Assigned" / "Unassigned". CHAP-417 deliberately did NOT
 *   add an owner picker for that reason — offering a control that writes an
 *   opaque UUID the page cannot render back is worse than omitting it.
 * - **Campaign target hero** uses `campaignPhases` from TenantConfig
 *   when populated; falls back to a sum of all phase target amounts.
 */
import { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTenantData } from '../hooks/useTenantData.js';
import { TenantLoading, TenantError } from '../components/TenantDataStates.js';
import { TierGuard } from '../components/RoleGuard.js';
import { formatCurrencyFull } from '../lib/formatters.js';
import { ApiError } from '../lib/api.js';
import {
  CHAPTERBUILD_OKR_STORE,
  latestOkrUpdate,
  formatLastUpdated,
  okrDivergenceNotice,
  okrAttributionNotice,
} from '../lib/okr-provenance.js';
import {
  useCreateInitiative,
  useUpdateInitiative,
  useDeleteInitiative,
  useCreateKeyResult,
  useUpdateKeyResult,
  useDeleteKeyResult,
  type InitiativeInput,
  type KeyResultInput,
} from '../hooks/useOkrs.js';
import type { OkrInitiative, OkrKeyResult } from '../../shared/types.js';

function parseNum(v: string | null): number {
  if (v == null) return 0;
  const n = Number(v.replace(/[,$]/g, ''));
  return Number.isFinite(n) ? n : 0;
}

function formatDue(iso: string | null): string {
  if (!iso) return '';
  if (iso === 'Monthly') return 'Monthly';
  try {
    const d = new Date(iso + 'T00:00:00');
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  } catch {
    return iso;
  }
}

function isOverdue(iso: string | null, current: number, target: number): boolean {
  if (!iso || iso === 'Monthly' || current >= target) return false;
  try {
    return new Date(iso + 'T00:00:00') < new Date();
  } catch {
    return false;
  }
}

/** Empty string -> null (clear the column); otherwise pass through. */
function orNull(v: string): string | null {
  const t = v.trim();
  return t === '' ? null : t;
}

const INPUT_CLASS =
  'w-full text-xs sm:text-sm px-2 py-1.5 rounded-lg border border-gray-200 focus:border-orange focus:outline-none min-w-0';
const BTN_PRIMARY =
  'text-xs px-3 py-1.5 rounded-lg bg-orange text-white font-semibold hover:bg-orange/90 transition-colors disabled:opacity-60 disabled:cursor-not-allowed';
const BTN_SUBTLE =
  'text-[11px] px-2.5 py-1 rounded-lg border border-gray-200 text-navy hover:border-orange transition-colors disabled:opacity-60 disabled:cursor-not-allowed';

export default function OKRs() {
  const { slug = '' } = useParams<{ slug: string }>();
  const { data, isLoading, error, refetch } = useTenantData(slug);
  const createInitiative = useCreateInitiative(slug);
  const [showCreate, setShowCreate] = useState(false);

  const campaignTotals = useMemo(() => {
    if (!data) return { target: 0, current: 0 };
    return data.campaignPhases.reduce(
      (acc, p) => ({
        target: acc.target + (p.targetAmount ?? 0),
        current: acc.current + (p.currentAmount ?? 0),
      }),
      { target: 0, current: 0 },
    );
  }, [data]);

  const lastUpdated = useMemo(() => latestOkrUpdate(data?.okrs ?? []), [data]);

  if (isLoading) return <TenantLoading />;
  if (error) return <TenantError message={String(error)} onRetry={() => refetch()} />;
  if (!data) return <PageState label="No data" />;

  const campaignPct = campaignTotals.target > 0
    ? Math.round((campaignTotals.current / campaignTotals.target) * 100)
    : 0;
  const initiatives = [...data.okrs].sort((a, b) => a.sortOrder - b.sortOrder);

  return (
    <div className="space-y-3 sm:space-y-4 min-w-0">
      <div className="flex items-start justify-between gap-3 min-w-0 flex-wrap">
        <div className="min-w-0">
          <h2 className="text-navy text-xl sm:text-2xl lg:text-3xl font-bold">Campaign OKRs</h2>
          <p className="text-neutral text-xs sm:text-sm mt-1">Objectives &amp; Key Results across campaign phases</p>
        </div>
        <TierGuard tier="cabinet">
          <button
            type="button"
            onClick={() => setShowCreate((s) => !s)}
            className={`${BTN_PRIMARY} flex-shrink-0`}
          >
            {showCreate ? 'Cancel' : 'New initiative'}
          </button>
        </TierGuard>
      </div>

      {/* CHAP-417 divergence guard — see the file docstring. */}
      <ProvenanceBanner lastUpdated={lastUpdated} />

      {showCreate && (
        <TierGuard tier="cabinet">
          <InitiativeForm
            title="New initiative"
            submitLabel="Create initiative"
            pending={createInitiative.isPending}
            error={createInitiative.error}
            onCancel={() => setShowCreate(false)}
            onSubmit={async (values) => {
              await createInitiative.mutateAsync({ ...values, name: values.name ?? '' });
              setShowCreate(false);
            }}
          />
        </TierGuard>
      )}

      {/* Campaign Target */}
      {campaignTotals.target > 0 && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 sm:p-6">
          <div className="flex items-center justify-between mb-2 min-w-0 flex-wrap gap-2">
            <h3 className="text-navy font-semibold text-sm sm:text-base">Fundraising Target</h3>
            <span className="text-xs sm:text-sm text-neutral">
              {formatCurrencyFull(campaignTotals.current)} of {formatCurrencyFull(campaignTotals.target)}
            </span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-3">
            <div className="bg-orange h-full rounded-full" style={{ width: `${Math.max(campaignPct, 1)}%` }} />
          </div>
          <p className="text-neutral text-xs mt-2">
            {campaignPct}% - across {data.campaignPhases.length} phase{data.campaignPhases.length === 1 ? '' : 's'}
          </p>
        </div>
      )}

      {/* Phase Timeline */}
      {data.campaignPhases.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 sm:p-6">
          <h3 className="text-navy font-semibold text-sm sm:text-base mb-3">Phase Timeline</h3>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            {data.campaignPhases
              .slice()
              .sort((a, b) => a.sortOrder - b.sortOrder)
              .map((p) => {
                const isActive = p.status === 'active';
                return (
                  <div
                    key={p.id}
                    className={`p-3 rounded-lg border min-w-0 ${
                      isActive ? 'bg-orange/5 border-orange' : 'bg-gray-50 border-gray-200'
                    }`}
                  >
                    <p className={`text-xs font-semibold uppercase ${isActive ? 'text-orange' : 'text-neutral'}`}>
                      {p.name}
                    </p>
                    {(p.periodStart || p.periodEnd) && (
                      <p className="text-navy font-bold text-xs sm:text-sm mt-1 truncate">
                        {p.periodStart ?? 'n/a'} → {p.periodEnd ?? 'n/a'}
                      </p>
                    )}
                    {isActive && <span className="text-xs text-orange font-medium">Active</span>}
                  </div>
                );
              })}
          </div>
        </div>
      )}

      {/* Initiatives */}
      {initiatives.length === 0 ? (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-800">
          No OKR initiatives seeded for this tenant.
        </div>
      ) : (
        initiatives.map((init) => <InitiativeCard key={init.id} init={init} slug={slug} />)
      )}
    </div>
  );
}

/**
 * The divergence guard's on-screen half. Shown to EVERY reader, not just
 * cabinet — a board member with no edit rights is precisely the person who
 * could otherwise mistake this copy for the only one.
 */
function ProvenanceBanner({ lastUpdated }: { lastUpdated: string | null }) {
  return (
    <div className="bg-navy/5 border border-navy/10 rounded-xl p-3 sm:p-4 min-w-0">
      <div className="flex items-start justify-between gap-3 flex-wrap min-w-0">
        <p className="text-xs sm:text-sm text-navy font-semibold min-w-0">
          Source: {CHAPTERBUILD_OKR_STORE.label}
        </p>
        {/*
          "This copy last updated", never "OKRs last updated". The timestamp
          describes the Postgres copy ONLY and says nothing about the board
          dashboard's freshness — a reader must not infer "updated just now,
          therefore current" while the other copy holds a newer, different value.
        */}
        <p className="text-xs text-neutral flex-shrink-0">
          {lastUpdated ? (
            <>
              This copy last updated{' '}
              <time dateTime={lastUpdated} className="font-medium text-navy">
                {formatLastUpdated(lastUpdated)}
              </time>
            </>
          ) : (
            <span className="font-medium text-navy">This copy&rsquo;s last update is unavailable</span>
          )}
        </p>
      </div>
      <p className="text-xs text-neutral mt-1.5">{okrDivergenceNotice()}</p>
      {/*
        Cabinet-only, and deliberately placed where the edit decision is made.
        See okrAttributionNotice() — this is what makes ChapterBuild the
        preferred writer without a flag.
      */}
      <TierGuard tier="cabinet">
        <p className="text-xs text-navy/80 mt-1.5 font-medium">{okrAttributionNotice()}</p>
      </TierGuard>
    </div>
  );
}

/**
 * The seed writes initiative status as 'in_progress' / 'not_started'
 * (seed/omega-zeta.ts:289-292) while campaign phases use 'active'
 * (:232). This card previously tested only for 'active', so every
 * initiative rendered greyed-out - including the two genuinely underway at
 * 65% and 40% - and the chip printed the raw snake_case value to the board.
 */
const ACTIVE_STATUSES = new Set(['active', 'in_progress']);

function humanizeStatus(status: string): string {
  return status.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

function InitiativeCard({ init, slug }: { init: OkrInitiative; slug: string }) {
  const [editing, setEditing] = useState(false);
  const [addingKr, setAddingKr] = useState(false);
  const updateInitiative = useUpdateInitiative(slug);
  const deleteInitiative = useDeleteInitiative(slug);
  const createKeyResult = useCreateKeyResult(slug);

  // 409 `cascade_confirmation_required` — the API refuses to destroy key
  // results implicitly. Hold the refusal so the user can confirm explicitly.
  const [cascadePrompt, setCascadePrompt] = useState<string | null>(null);

  const isActive = init.status ? ACTIVE_STATUSES.has(init.status) : false;
  const pct = init.targetProgress && init.targetProgress > 0
    ? Math.min(100, Math.round((init.currentProgress / init.targetProgress) * 100))
    : 0;

  const runDelete = async (cascade: boolean) => {
    try {
      await deleteInitiative.mutateAsync({ id: init.id, cascade });
      setCascadePrompt(null);
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        const body = err.body as { message?: string } | null;
        setCascadePrompt(body?.message ?? 'This initiative still has key results.');
        return;
      }
      throw err;
    }
  };

  return (
    <div
      className={`rounded-xl shadow-sm border p-4 sm:p-6 ${
        isActive ? 'bg-white border-gray-100' : 'bg-gray-50 border-gray-200'
      }`}
    >
      <div className="flex items-start justify-between gap-2 mb-3 min-w-0 flex-wrap">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="text-navy font-bold text-sm sm:text-base">{init.name}</h3>
            {init.phase && (
              <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                isActive ? 'bg-orange/10 text-orange' : 'bg-gray-200 text-gray-600'
              }`}>
                {init.phase}
              </span>
            )}
            {init.status && (
              <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                isActive ? 'bg-positive/10 text-positive' : 'bg-gray-200 text-gray-600'
              }`}>
                {humanizeStatus(init.status)}
              </span>
            )}
          </div>
        </div>
        <TierGuard tier="cabinet">
          <div className="flex items-center gap-2 flex-shrink-0">
            <button type="button" className={BTN_SUBTLE} onClick={() => setEditing((s) => !s)}>
              {editing ? 'Cancel' : 'Edit'}
            </button>
            <button
              type="button"
              className={BTN_SUBTLE}
              disabled={deleteInitiative.isPending}
              onClick={() => void runDelete(false)}
            >
              Delete
            </button>
          </div>
        </TierGuard>
      </div>

      {cascadePrompt && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-3 text-xs text-amber-900">
          <p>{cascadePrompt}</p>
          <div className="flex gap-2 mt-2">
            <button
              type="button"
              className={BTN_SUBTLE}
              disabled={deleteInitiative.isPending}
              onClick={() => void runDelete(true)}
            >
              Delete initiative and its key results
            </button>
            <button type="button" className={BTN_SUBTLE} onClick={() => setCascadePrompt(null)}>
              Keep it
            </button>
          </div>
        </div>
      )}

      {deleteInitiative.error && !cascadePrompt && (
        <p className="text-[11px] text-red-600 mb-2">{deleteInitiative.error.message}</p>
      )}

      {editing && (
        <InitiativeForm
          title="Edit initiative"
          submitLabel="Save"
          initial={init}
          pending={updateInitiative.isPending}
          error={updateInitiative.error}
          onCancel={() => setEditing(false)}
          onSubmit={async (values) => {
            await updateInitiative.mutateAsync({ id: init.id, patch: values });
            setEditing(false);
          }}
        />
      )}

      <div className="mb-3">
        <div className="flex justify-between text-xs mb-1">
          <span className="text-neutral">Progress</span>
          <span className="text-navy font-medium">
            {init.currentProgress} / {init.targetProgress ?? 'n/a'} ({pct}%)
          </span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2">
          <div
            className={`h-2 rounded-full ${isActive ? 'bg-orange' : 'bg-gray-400'}`}
            style={{ width: `${pct}%` }}
          />
        </div>
      </div>

      <div className="space-y-2">
        {init.keyResults.length === 0 ? (
          <p className="text-xs text-neutral italic">No key results yet.</p>
        ) : (
          init.keyResults
            .slice()
            .sort((a, b) => a.sortOrder - b.sortOrder)
            .map((kr) => <KrRow key={kr.id} kr={kr} slug={slug} />)
        )}
      </div>

      <TierGuard tier="cabinet">
        <div className="mt-3 border-t border-gray-100 pt-3">
          {addingKr ? (
            <KeyResultForm
              title="New key result"
              submitLabel="Add key result"
              pending={createKeyResult.isPending}
              error={createKeyResult.error}
              onCancel={() => setAddingKr(false)}
              onSubmit={async (values) => {
                await createKeyResult.mutateAsync({
                  ...values,
                  initiativeId: init.id,
                  name: values.name ?? '',
                });
                setAddingKr(false);
              }}
            />
          ) : (
            <button type="button" className={BTN_SUBTLE} onClick={() => setAddingKr(true)}>
              + Add key result
            </button>
          )}
        </div>
      </TierGuard>
    </div>
  );
}

function KrRow({ kr, slug }: { kr: OkrKeyResult; slug: string }) {
  const [editing, setEditing] = useState(false);
  const updateKeyResult = useUpdateKeyResult(slug);
  const deleteKeyResult = useDeleteKeyResult(slug);

  const current = parseNum(kr.current);
  const target = parseNum(kr.target);
  const krPct = target > 0 ? Math.min(100, Math.round((current / target) * 100)) : 0;
  const isComplete = target > 0 && current >= target;
  const overdue = isOverdue(kr.dueDate, current, target);

  if (editing) {
    return (
      <div className="bg-gray-50 rounded-lg p-2.5 min-w-0">
        <KeyResultForm
          title="Edit key result"
          submitLabel="Save"
          initial={kr}
          pending={updateKeyResult.isPending}
          error={updateKeyResult.error}
          onCancel={() => setEditing(false)}
          onSubmit={async (values) => {
            await updateKeyResult.mutateAsync({ id: kr.id, patch: values });
            setEditing(false);
          }}
        />
      </div>
    );
  }

  return (
    <div className="bg-gray-50 rounded-lg p-2.5 min-w-0">
      <div className="flex items-start justify-between gap-2 min-w-0 flex-wrap">
        <span className="text-gray-700 text-xs sm:text-sm min-w-0 flex-1">{kr.name}</span>
        <span className="text-neutral text-xs whitespace-nowrap flex-shrink-0">
          {kr.current ?? 0} / {kr.target ?? 'n/a'}{kr.unit ? ` ${kr.unit}` : ''}
        </span>
      </div>
      <div className="w-full bg-gray-200 rounded-full h-1.5 mt-1.5">
        <div
          className={`h-1.5 rounded-full ${isComplete ? 'bg-positive' : 'bg-orange'}`}
          style={{ width: `${krPct}%` }}
        />
      </div>
      {(kr.ownerUserId || kr.dueDate || kr.note) && (
        <div className="flex items-center gap-2 mt-1 flex-wrap">
          {kr.ownerUserId ? (
            <span className="text-xs text-neutral">Assigned</span>
          ) : (
            <span className="text-xs text-amber-700 italic">Unassigned</span>
          )}
          {kr.dueDate && (
            <span className={`text-xs ${overdue ? 'text-orange font-medium' : 'text-neutral'}`}>
              Due: {formatDue(kr.dueDate)}
            </span>
          )}
          {kr.note && <span className="text-xs text-neutral italic truncate">{kr.note}</span>}
        </div>
      )}

      <TierGuard tier="cabinet">
        <div className="flex items-center gap-2 mt-2">
          <button type="button" className={BTN_SUBTLE} onClick={() => setEditing(true)}>
            Edit
          </button>
          <button
            type="button"
            className={BTN_SUBTLE}
            disabled={deleteKeyResult.isPending}
            onClick={() => deleteKeyResult.mutate({ id: kr.id })}
          >
            Delete
          </button>
        </div>
      </TierGuard>
      {deleteKeyResult.error && (
        <p className="text-[11px] text-red-600 mt-1">{deleteKeyResult.error.message}</p>
      )}
    </div>
  );
}

/**
 * Initiative create/edit form. `status` offers only the canonical D1
 * vocabulary ('active' / 'planned') — the router accepts the two legacy seed
 * values and normalizes them forward, so there is no reason to let a new write
 * introduce more of them.
 */
function InitiativeForm({
  title,
  submitLabel,
  initial,
  pending,
  error,
  onCancel,
  onSubmit,
}: {
  title: string;
  submitLabel: string;
  initial?: OkrInitiative;
  pending: boolean;
  error: Error | null;
  onCancel: () => void;
  onSubmit: (values: InitiativeInput & { name?: string }) => Promise<void>;
}) {
  const [name, setName] = useState(initial?.name ?? '');
  const [phase, setPhase] = useState(initial?.phase ?? '');
  const [status, setStatus] = useState(initial?.status ?? '');
  const [targetProgress, setTargetProgress] = useState(
    initial?.targetProgress != null ? String(initial.targetProgress) : '',
  );
  const [currentProgress, setCurrentProgress] = useState(String(initial?.currentProgress ?? 0));

  const canSubmit = name.trim().length > 0 && !pending;

  return (
    <form
      className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 space-y-2.5 min-w-0 mb-3"
      onSubmit={async (e) => {
        e.preventDefault();
        if (!canSubmit) return;
        await onSubmit({
          name: name.trim(),
          phase: orNull(phase),
          status: orNull(status),
          targetProgress: targetProgress.trim() === '' ? null : Number(targetProgress),
          currentProgress: Number(currentProgress || 0),
        });
      }}
    >
      <h4 className="text-navy font-semibold text-sm">{title}</h4>

      <label className="block">
        <span className="text-xs text-neutral">Name</span>
        <input className={INPUT_CLASS} value={name} onChange={(e) => setName(e.target.value)} required maxLength={255} />
      </label>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <label className="block">
          <span className="text-xs text-neutral">Phase</span>
          <input className={INPUT_CLASS} value={phase} onChange={(e) => setPhase(e.target.value)} maxLength={32} />
        </label>
        <label className="block">
          <span className="text-xs text-neutral">Status</span>
          <select className={INPUT_CLASS} value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="">n/a</option>
            <option value="active">Active</option>
            <option value="planned">Planned</option>
          </select>
        </label>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <label className="block">
          <span className="text-xs text-neutral">Current progress (%)</span>
          <input
            className={INPUT_CLASS}
            type="number"
            min={0}
            max={100}
            value={currentProgress}
            onChange={(e) => setCurrentProgress(e.target.value)}
          />
        </label>
        <label className="block">
          <span className="text-xs text-neutral">Target progress (%)</span>
          <input
            className={INPUT_CLASS}
            type="number"
            min={0}
            max={100}
            value={targetProgress}
            onChange={(e) => setTargetProgress(e.target.value)}
          />
        </label>
      </div>

      {error && <p className="text-[11px] text-red-600">{error.message}</p>}

      <div className="flex gap-2 pt-1">
        <button type="submit" className={BTN_PRIMARY} disabled={!canSubmit}>
          {pending ? 'Saving…' : submitLabel}
        </button>
        <button type="button" className={BTN_SUBTLE} onClick={onCancel}>
          Cancel
        </button>
      </div>
    </form>
  );
}

/**
 * Key-result create/edit form.
 *
 * No owner picker: `ownerUserId` is an opaque UUID this page cannot render
 * back as a name (see the file docstring), so a control that writes one would
 * produce a value the board cannot read. It stays server-editable until the
 * team endpoint lands.
 */
function KeyResultForm({
  title,
  submitLabel,
  initial,
  pending,
  error,
  onCancel,
  onSubmit,
}: {
  title: string;
  submitLabel: string;
  initial?: OkrKeyResult;
  pending: boolean;
  error: Error | null;
  onCancel: () => void;
  onSubmit: (values: KeyResultInput & { name?: string }) => Promise<void>;
}) {
  const [name, setName] = useState(initial?.name ?? '');
  const [current, setCurrent] = useState(initial?.current ?? '');
  const [target, setTarget] = useState(initial?.target ?? '');
  const [unit, setUnit] = useState(initial?.unit ?? '');
  // `due_date` is a Postgres date; the router rejects anything that is not
  // YYYY-MM-DD, which is exactly what <input type="date"> emits.
  const [dueDate, setDueDate] = useState(
    initial?.dueDate && /^\d{4}-\d{2}-\d{2}$/.test(initial.dueDate) ? initial.dueDate : '',
  );
  const [note, setNote] = useState(initial?.note ?? '');

  const canSubmit = name.trim().length > 0 && !pending;

  return (
    <form
      className="space-y-2.5 min-w-0"
      onSubmit={async (e) => {
        e.preventDefault();
        if (!canSubmit) return;
        await onSubmit({
          name: name.trim(),
          current: orNull(current),
          target: orNull(target),
          unit: orNull(unit),
          dueDate: orNull(dueDate),
          note: orNull(note),
        });
      }}
    >
      <h4 className="text-navy font-semibold text-xs">{title}</h4>

      <label className="block">
        <span className="text-xs text-neutral">Key result</span>
        <input className={INPUT_CLASS} value={name} onChange={(e) => setName(e.target.value)} required maxLength={255} />
      </label>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
        <label className="block">
          <span className="text-xs text-neutral">Current</span>
          <input className={INPUT_CLASS} value={current} onChange={(e) => setCurrent(e.target.value)} inputMode="decimal" />
        </label>
        <label className="block">
          <span className="text-xs text-neutral">Target</span>
          <input className={INPUT_CLASS} value={target} onChange={(e) => setTarget(e.target.value)} inputMode="decimal" />
        </label>
        <label className="block">
          <span className="text-xs text-neutral">Unit</span>
          <input className={INPUT_CLASS} value={unit} onChange={(e) => setUnit(e.target.value)} maxLength={32} />
        </label>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <label className="block">
          <span className="text-xs text-neutral">Due date</span>
          <input className={INPUT_CLASS} type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} />
        </label>
        <label className="block">
          <span className="text-xs text-neutral">Note</span>
          <input className={INPUT_CLASS} value={note} onChange={(e) => setNote(e.target.value)} maxLength={4000} />
        </label>
      </div>

      {error && <p className="text-[11px] text-red-600">{error.message}</p>}

      <div className="flex gap-2 pt-1">
        <button type="submit" className={BTN_PRIMARY} disabled={!canSubmit}>
          {pending ? 'Saving…' : submitLabel}
        </button>
        <button type="button" className={BTN_SUBTLE} onClick={onCancel}>
          Cancel
        </button>
      </div>
    </form>
  );
}

function PageState({ label, detail, tone }: { label: string; detail?: string; tone?: 'error' }) {
  const color = tone === 'error' ? 'text-red-700' : 'text-neutral';
  return (
    <div className="p-8 text-center">
      <p className={`text-sm ${color}`}>{label}</p>
      {detail && <pre className="text-xs text-gray-500 mt-2 whitespace-pre-wrap">{detail}</pre>}
    </div>
  );
}
